import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, QrCode, Users, Dice5, ArrowRight, Zap } from "lucide-react";

const Feature = ({ icon: Icon, title, body, tint }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4 }}
    className="card-paper p-7 relative"
  >
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
      style={{ background: tint }}
    >
      <Icon className="w-6 h-6 text-slate-900" />
    </div>
    <h3 className="font-display text-xl sm:text-2xl font-semibold text-slate-800 mb-2">{title}</h3>
    <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{body}</p>
  </motion.div>
);

export default function Landing() {
  return (
    <div className="min-h-screen paper-bg">
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center rotate-3">
            <Dice5 className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-xl tracking-tight text-slate-900">
            Random Roster
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/join"
            data-testid="nav-join-link"
            className="hidden sm:inline-flex text-sm font-semibold text-slate-700 hover:text-indigo-600"
          >
            Join a class
          </Link>
          <Link
            to="/login"
            data-testid="nav-login-link"
            className="btn-secondary text-sm py-2 px-4"
          >
            Teacher sign in
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 lg:pt-16 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 mb-6"
          >
            <Sparkles className="w-3.5 h-3.5" /> Zero setup for students
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            data-testid="landing-hero-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]"
          >
            Pick a random student.{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Fairly.</span>
              <span
                className="absolute inset-x-0 bottom-1 h-3 bg-amber-300/70 -skew-x-3 z-0"
                aria-hidden
              />
            </span>{" "}
            <br className="hidden sm:block" />
            Every time.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed"
          >
            Project a QR code. Students scan, type their name, done. When you&apos;re ready, hit
            <span className="font-semibold text-slate-900"> Randomize & Reveal </span>
            and every phone lights up with a unique number from 1 to N.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link to="/login" data-testid="cta-start-class" className="btn-primary inline-flex items-center gap-2">
              Start a class <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/join" data-testid="cta-student-join" className="btn-secondary inline-flex items-center gap-2">
              I&apos;m a student
            </Link>
          </motion.div>
          <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Live WebSocket sync
            </div>
            <div>No student accounts</div>
            <div>Free to use</div>
          </div>
        </div>

        {/* Illustration mock */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, rotate: -3, y: 30 }}
            animate={{ opacity: 1, rotate: 0, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="card-paper p-6 sm:p-8 relative rotate-1">
              <div className="tape left-8 -top-3" />
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600 mb-3">
                Live Class · MATH 101
              </div>
              <div className="font-display text-3xl font-extrabold text-slate-900 mb-5">
                24 students joined
              </div>
              <div className="grid grid-cols-4 gap-2 mb-5">
                {["Ava", "Ben", "Cai", "Dee", "Eli", "Faye", "Gus", "Hana"].map((n, i) => (
                  <div
                    key={n}
                    className="bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-2 text-xs font-semibold text-indigo-800 text-center"
                    style={{ transform: `rotate(${(i % 2 ? 1 : -1) * 1.5}deg)` }}
                  >
                    {n}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Accepting
                </span>
                <button className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2">
                  <Zap className="w-4 h-4" /> Reveal
                </button>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="card-paper p-5 absolute -bottom-8 -left-6 w-44 -rotate-6 hidden sm:block"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
                Ava&apos;s phone
              </div>
              <div className="font-mono-num text-5xl font-black text-indigo-600 leading-none">
                07
              </div>
              <div className="text-xs text-slate-500 mt-2">You&apos;re number 7</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="max-w-2xl mb-12">
          <span className="pill bg-indigo-50 text-indigo-700 border border-indigo-200 mb-4">
            How it works
          </span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Three steps. About forty seconds.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          <Feature
            icon={QrCode}
            title="Project the QR code"
            body="Sign in and share your dashboard on the projector. Students see a big scannable code."
            tint="#EEF2FF"
          />
          <Feature
            icon={Users}
            title="Students join by name"
            body="They scan, type their first name, and tap Join. No accounts, no downloads, no logins."
            tint="#ECFDF5"
          />
          <Feature
            icon={Dice5}
            title="Randomize & Reveal"
            body="One click. Every phone shows a unique number from 1 to N. Perfect for turns, groups, and quizzes."
            tint="#FEF3C7"
          />
        </div>
      </section>

      <footer className="border-t border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div className="font-display font-bold text-slate-700">Random Roster</div>
          <div>Made for classrooms · Fair, fast, and free.</div>
        </div>
      </footer>
    </div>
  );
}
